import { Form, Formik } from "formik";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { Box, Button, CircularProgress, Grid, Link, Typography } from "@mui/material";

import AuthLogos from "@components/AuthLogos";
import Checkbox from "@components/Forms/FormInputs/CheckBox";
import TextField from "@components/Forms/FormInputs/TextField";
import SignupSuccess from "@components/SignupSuccess";

import { usePopupActions } from "@store/popupStore";

import contentCardStyles from "@theme/contentCardStyles";
import theme from "@theme/index";

import userService from "../services/user.service";

const SignUpSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Required"),
  firstName: Yup.string().required("Required"),
  isPrivacyPolicyAccepted: Yup.boolean().oneOf([true], "You must accept ScaleIP's privacy policy"),
  isTermsOfServiceAccepted: Yup.boolean().oneOf([true], "You must accept ScaleIP's terms of service"),
  lastName: Yup.string().required("Required"),
  password: Yup.string()
    .min(8, "Must be at least 8 characters")
    .max(20, "Must be less than 20 characters")
    .required("Required"),
});

function SignUp() {
  const navigate = useNavigate();
  const popupActions = usePopupActions();

  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 4,
          justifyContent: "center",
          overflowY: "auto",
          p: {
            sm: 4,
            xs: 2,
          },
        }}
      >
        <AuthLogos />
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          sx={{
            ...contentCardStyles,
            border: `1px solid ${theme.palette.grey[300]}`,
            display: "flex",
            flexDirection: "column",
            flexGrow: "unset",
            gap: 2,
            height: "fit-content",
            p: {
              sm: 5,
              xs: 4,
            },
            width: {
              sm: "560px",
              xs: "95%",
            },
          }}
        >
          <Typography
            align="center"
            sx={{
              mb: 2,
            }}
            variant="h3"
          >
            Sign Up
          </Typography>
          <Formik
            initialValues={{
              email: "",
              firstName: "",
              isPrivacyPolicyAccepted: false,
              isTermsOfServiceAccepted: false,
              lastName: "",
              password: "",
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await userService.signUp(values.email, values.password, values.firstName, values.lastName, navigate);
                popupActions.openModal({
                  content: <SignupSuccess email={values.email} />,
                  title: "Account Created",
                });
                navigate("/login");
              } catch {
                setSubmitting(false);
              }
            }}
            validationSchema={SignUpSchema}
          >
            {({ dirty, isSubmitting, isValid }) => (
              <Box
                component={Form}
                display="flex"
                flexDirection="column"
                sx={{
                  gap: 1,
                  width: "100%", // Fix IE 11 issue.
                }}
              >
                <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                    <TextField isFullWidth isRequired label="First Name" name="firstName" size="small" type="text" />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <TextField isFullWidth isRequired label="Last Name" name="lastName" size="small" type="text" />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <TextField isFullWidth isRequired label="Work Email" name="email" size="small" type="email" />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <TextField isFullWidth isRequired label="Password" name="password" size="small" type="password" />
                  </Grid>
                </Grid>
                <Typography sx={{ fontWeight: 600, mt: 3 }}>
                  Please review and agree to our terms before continuing:
                </Typography>
                <Box>
                  <Checkbox
                    label={
                      <Typography>
                        I have read and agree to{" "}
                        <Link href="https://www.scaleip.com/privacy-policy" target="_blank">
                          ScaleIP&apos;s privacy policy
                        </Link>
                        .
                      </Typography>
                    }
                    name="isPrivacyPolicyAccepted"
                  />
                  <Checkbox
                    label={
                      <Typography>
                        I have read and agree to{" "}
                        <Link href="https://www.scaleip.com/terms-of-service" target="_blank">
                          ScaleIP&apos;s terms of service
                        </Link>
                        .
                      </Typography>
                    }
                    name="isTermsOfServiceAccepted"
                  />
                </Box>
                <Button
                  disabled={!isValid || !dirty || isSubmitting}
                  fullWidth
                  size="large"
                  sx={{ mt: 3 }}
                  type="submit"
                  variant="contained"
                >
                  {isSubmitting ? <CircularProgress size={20} /> : <>Create account</>}
                </Button>
              </Box>
            )}
          </Formik>
          <Typography align="center" sx={{ fontWeight: 600, mt: 3 }} variant="h5">
            Have an account?{" "}
            <Link component={RouterLink} to="../login">
              Log in
            </Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default SignUp;
